import "./style.scss";

import { Tab } from "@mui/material";
import BlockModal from "components/shared/BlockModal";
import SaveButton from "components/shared/SaveButton";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import { StyledTabs } from "components/shared/StyledTabs";
import { PortalType } from "contexts/portal.context";
import { sortAlphabetically } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker } from "react-router-dom";
import { fetchDeleteEntry, fetchEntries, fetchUpdateEntry } from "store/accountingStore";
import { fetchBrands } from "store/brandStore";
import { putCompanySettings } from "store/settingsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";
import type { AccountingEntry, AccountingMapping } from "store/types";

import { AccountingSkeleton } from "./AccountingSkeleton";
import AccountingTypology from "./AccountingTypology";
import { useAccountingTypologyFilters } from "./ModalContent/AddFilter/useAccountingFilters";

const TABS = {
    payment: "payment",
    defer: "defer",
    advanceReduction: "advanceReduction",
    allowance: "allowance",
} as const satisfies Record<AccountingMapping["exportType"], AccountingMapping["exportType"]>;

const AccountingSettings = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const portalType = useContext(PortalType);
    const { entries, isLoading } = useSelector((state: RootState) => state.accounting);
    const brands = useAppSelector((state) => state.brands.brands);

    const [tabsValue, setTabsValue] = useState<AccountingMapping["exportType"]>("payment");

    const { filters, filterOptions } = useAccountingTypologyFilters(portalType, brands, tabsValue);

    const [isTypologyEdited, setIsTypologyEdited] = useState<Record<number, AccountingEntry>>({});

    const { getCompanySettings, companyInfos, usesCegidGect } = useCompanySettings();

    const [accountingGroupMethod, setAccountingGroupMethod] = useState(
        getCompanySettings("preferred_accounting_group_method"),
    );
    const usesAccountingWithChannelDispatch = getCompanySettings("uses_accounting_with_channel_dispatch");
    const usesBrands = getCompanySettings("uses_multiple_brands");
    const companyDetails = { usesAccountingWithChannelDispatch, usesBrands, companyInfos };

    const entriesNames: string[] = entries.map((entry) => entry.name);

    const accountingGroupMethods = [
        { value: "beneficiary", description: t("CompanySettings.AccountingGroupBeneficiary") },
        { value: "term", description: t("CompanySettings.AccountingGroupTerm") },
        { value: "typology", description: t("CompanySettings.AccountingGroupTypology") },
    ];

    const blocker = useBlocker(Object.entries(isTypologyEdited).length > 0);

    const sortedEntries = useMemo(() => {
        const filteredEntries = entries.filter((entry) => {
            switch (tabsValue) {
                case TABS.advanceReduction:
                case TABS.allowance:
                    return (
                        !entry.filters.relatedResource ||
                        !["advance", "advanceForfait"].includes(entry.filters.relatedResource)
                    );
                case TABS.payment:
                case TABS.defer:
                default:
                    return true;
            }
        });

        const alphabeticalEntries = sortAlphabetically(
            filteredEntries.filter((entry) => entry.name !== "Par défaut"),
            "name",
        );
        const defaultEntries = filteredEntries.filter((entry) => entry.name === "Par défaut");
        alphabeticalEntries.unshift(...defaultEntries);
        return alphabeticalEntries;
    }, [entries, tabsValue]);

    const { paginatedData: paginatedEntries, paginatedElRef } = useInfiniteScroll(sortedEntries, true, 5);

    useEffect(() => {
        dispatch(fetchEntries());
        // dispatch(fetchTypologies()); I can't remember why I fetched this ... Need to look into it :)
    }, []);

    const handleEntryDeletion = async (_entry: AccountingEntry) => {
        if (_entry.id) {
            await dispatch(fetchDeleteEntry(_entry));
        }
    };

    const handleChangeTypology = (data: AccountingEntry, idx: number) => {
        setIsTypologyEdited({
            ...isTypologyEdited,
            [idx]: data,
        });
    };

    const handleAccountingMethodChange = (event: any) => {
        const value = event.target.value;
        setAccountingGroupMethod(value);
        dispatch(
            putCompanySettings([
                {
                    settingsName: "preferred_accounting_group_method",
                    settingsValue: value,
                },
            ]),
        );
    };

    const updateEntries = () => {
        const updatedEntries: AccountingEntry[] = [];

        Object.values(isTypologyEdited as Record<number, AccountingEntry>).forEach((value) => {
            updatedEntries.push(value);
        });

        dispatch(fetchUpdateEntry(updatedEntries));
        setIsTypologyEdited([]);
        blocker.reset?.();
    };

    useEffect(() => {
        if (usesBrands) {
            dispatch(fetchBrands(true));
        }
    }, [usesBrands]);

    return (
        <div className="accounting-settings" ref={paginatedElRef}>
            <BlockModal blocker={blocker} />
            <div className="accounting-settings-header">
                <h1>{t("Settings.Accounting")}</h1>
            </div>
            <section className="history-main-tabs">
                <StyledTabs
                    value={tabsValue}
                    onChange={(_, v) => {
                        setTabsValue(v);
                    }}
                >
                    <Tab value={TABS.payment} label={t("Accounting.Payment")}></Tab>
                    <Tab value={TABS.defer} label={t("Accounting.Deferment")}></Tab>
                    <Tab value={TABS.advanceReduction} label={t("Accounting.ReductionOfAdvance")}></Tab>
                    <Tab value={TABS.allowance} label={t("Accounting.Allowance")}></Tab>
                </StyledTabs>
            </section>
            <div className="accounting-settings-preference">
                <span>{t("CompanySettings.AccountingGroupMethod")}</span>
                <StyledSelect
                    className="preference-select"
                    size="small"
                    sx={{ width: "50%" }}
                    value={usesCegidGect ? "term" : accountingGroupMethod || "beneficiary"}
                    disabled={usesCegidGect}
                    onChange={(ev) => handleAccountingMethodChange(ev)}
                >
                    {accountingGroupMethods.map((group, i) => {
                        return (
                            <StyledMenuItem key={`accounting-group-${group.value}`} value={group.value}>
                                {group.description}
                            </StyledMenuItem>
                        );
                    })}
                </StyledSelect>
            </div>
            <div className="accounting-settings-main">
                {isLoading ? (
                    <AccountingSkeleton />
                ) : (
                    paginatedEntries?.map((entry, idx) => {
                        return (
                            <>
                                <AccountingTypology
                                    propEntry={entry}
                                    key={entry.id}
                                    exportType={tabsValue}
                                    onDeleteEntry={(data: AccountingEntry) => handleEntryDeletion(data)}
                                    onChange={(data: AccountingEntry) => handleChangeTypology(data, idx)}
                                    companyDetails={companyDetails}
                                    isDefaultTypology={idx === 0}
                                    brands={brands}
                                    entriesNames={entriesNames}
                                    accountingGroupMethod={usesCegidGect ? "term" : accountingGroupMethod}
                                    filters={filters}
                                    filterOptions={filterOptions}
                                />
                            </>
                        );
                    })
                )}
            </div>
            {Object.entries(isTypologyEdited as Record<number, AccountingEntry>).length > 0 && (
                <SaveButton onSave={updateEntries} buttonText={t("Settings.SaveChanges")} />
            )}
        </div>
    );
};

export default AccountingSettings;
